import type { I18n } from 'vue-i18n';

/**
 * 套餐版本类型
 */
export enum EditionType {
    BASIC = 'basic',
    PRO = 'pro',
    ENTERPRISE = 'enterprise',
    API = 'api'
}

export interface PlgPlanSDKOptions {
    i18n?: I18n;
}

export interface PlgPlanSDKOpenOptions {
    withoutClose?: boolean;
    successUrl?: string;
    cancelUrl?: string;
    viewMoreUrl?: string;
}

export enum PACKAGE_FEATURES {
    AI_POWERED_QA = 'AI_POWERED_QA',
    TRIZ_SOLUTIONS = 'TRIZ_SOLUTIONS',
    PRE_RESEARCH_REPORTS = 'PRE_RESEARCH_REPORTS',
    AI_FMEA = 'AI_FMEA',
    AI_MONITORING_INSIGHTS = 'AI_MONITORING_INSIGHTS',
    PATENT_SUMMARY = 'PATENT_SUMMARY',
    COLLABORATION_TOOLS = 'COLLABORATION_TOOLS',
    INCOGNITO_MODE = 'INCOGNITO_MODE',
    PATENT_SEARCH = 'PATENT_SEARCH',
    AI_TECHNICAL_DISCLOSURE_ASSISTANT = 'AI_TECHNICAL_DISCLOSURE_ASSISTANT',
    AI_NOVELTY_SEARCH = 'AI_NOVELTY_SEARCH',
    FIRST_PRINCIPLE_SOLUTIONS = 'FIRST_PRINCIPLE_SOLUTIONS',
    MATERIAL_PROPERTIES_EXPLORATION = 'MATERIAL_PROPERTIES_EXPLORATION',
    SUBSTANCE_INQUIRY = 'SUBSTANCE_INQUIRY',
    METAL_COMPOSITION_SEARCH = 'METAL_COMPOSITION_SEARCH',
    STRUCTURE_SEARCH = 'STRUCTURE_SEARCH',
    MATERIAL_SYNTHESIS_EXPLORATION = 'MATERIAL_SYNTHESIS_EXPLORATION',
    PERFORMANCE_ANALYSIS = 'PERFORMANCE_ANALYSIS',
    COMPANY_SEARCH = 'COMPANY_SEARCH',
    MUTIPLE_SOURCES = 'MUTIPLE_SOURCES',
    COMPETITIVE_LANDSCAPE = 'COMPETITIVE_LANDSCAPE',
    LS_PHARMA_INSIGHTS_ADVISOR = 'LS_PHARMA_INSIGHTS_ADVISOR',
    LS_PHARMA_DOCUMENT_ANALYZER = 'LS_PHARMA_DOCUMENT_ANALYZER',
    LS_MARKUSH_CLAIM_DRAFTING = 'LS_MARKUSH_CLAIM_DRAFTING',
    LS_GENERIC_DRUG_ID_CHECKER = 'LS_GENERIC_DRUG_ID_CHECKER',
    LS_DRUGGABLE_ANTIBODY_FINDER = 'LS_DRUGGABLE_ANTIBODY_FINDER',
    LS_GENERICS_TO_INNOVATE_NAVIGATOR = 'LS_GENERICS_TO_INNOVATE_NAVIGATOR',

    IP_NOVELTY = 'IP_NOVELTY',
    IP_SPECIFICATION = 'IP_SPECIFICATION',
    IP_DESIGN_INFRINGEMENT = 'IP_DESIGN_INFRINGEMENT',
    IP_INFRINGEMENT = 'IP_INFRINGEMENT',
    IP_TRANSLATION = 'IP_TRANSLATION'
}

export enum PAID_STATUS {
    LOW_VERSION = 0,
    PAID = 1,
    UNPAID = 2,
    COMMING_SOON = 3
}

/**
 * Eureka 套餐类型枚举
 */
export enum EurekaPackageType {
    PROTECT_FREE = 'protect_free',
    PROTECT_TRIAL = 'protect_trial',
    PROTECT_COMMERCIAL_OLD = 'protect_commercial_old',
    PROTECT_COMMERCIAL = 'protect_commercial',

    IP_COMMERCIAL = 'ip_commercial',

    MATERIAL_TRIAL = 'material_trial',
    MATERIAL_COMMERCIAL_OLD = 'material_commercial_old',
    MATERIAL_COMMERCIAL = 'material_commercial',
    MATERIAL_COMMERCIAL_RAPID_SEARCH = 'material_commercial_rapid_search',

    AIR_FREE = 'air_free',
    AIR_TRIAL = 'air_trial',
    AIR_COMMERCIAL = 'air_commercial',
    AIR_COMMERCIAL_PROFESSIONAL_EDITION = 'air_commercial_professional_edition',
    AIR_COMMERCIAL_NAVIGATOR_EDITION = 'air_commercial_navigator_edition',

    INSIGHTS_TRIAL = 'insights_trial',
    INSIGHTS_COMMERCIAL = 'insights_commercial',

    LS_TRIAL = 'ls_trial',
    LS_COMMERCIAL = 'ls_commercial',
}

export interface ICreatePaymentSessionRequest {
    price_ids: string[];
    mode?: 'subscription';
    ui_mode?: 'hosted' | 'custom' | 'embedded';
    return_url?: string;
    success_url?: string;
    cancel_url?: string;
    automatic_tax?: boolean;
    trial_days?: number;
}

export interface ICreatePaymentSessionResponse {
    customer_id: string;
    url: string;
    clint_secret: string;
}

export interface ICancelSubscriptionRequest {
    subscription_id: string;
    reason?: string;
    remark?: string;
}

export interface IResumeSubscriptionRequest {
    subscription_id: string;
}

export interface IGetInvoicesRequest {
    subscription_id: string;
    action_type?: string;
    return_url?: string;
}

export interface ISubscriptionItem {
    amount: number;
    concurrency: string;
    current_period_end: string;
    current_period_start: string;
    interval_type: string;
    price_id: string;
    product_name: string;
}

export enum ESubscriptionStatus {
    'trialing' = 'trialing',
    'active' = 'active',
    'canceled' = 'canceled',
    'ended' = 'ended',
    'past_due' = 'past_due',
    'unpaid' = 'unpaid'
}

export interface ISubscriptionListDTO {
    subscription_id: string;
    subscription_status: ESubscriptionStatus;
    created_at: string;
    current_period_start: string;
    current_period_end: string;
    cancel_at?: string;
    items: ISubscriptionItem[]
}

/**
 * 多语言文本对象
 */
export interface LocaleText {
    cn: string;
    en: string;
    jp: string;
    tw: string;
}

/**
 * 功能分组
 */
export interface FeatureGroup {
    /** 分组标题（可选，如果为 undefined 则不显示标题） */
    title?: LocaleText;
    /** 分组内的功能列表 */
    features: LocaleText[];
}

export interface PricingPlan {
    editionType: EditionType;
    price?: number | string;
    originalPrice?: number | null;
    yearSaveAmount?: number;
    priceUnit: string;
    priceNote?: string;
    highlighted: boolean;
    buttonStyle: 'primary' | 'outline';
    badge: string | null;
    disabled: boolean;
    isRecommended: boolean;
    isCurrent: boolean;
    /** 功能列表（支持分组） */
    features: (LocaleText | FeatureGroup)[];
}

export interface PlanConfig {
    /** 积分数量 */
    credits: number;
    /** 月付原价 */
    originalPrice: number;
    /** 月付售价 */
    salePrice: number;
    /** 年付原价 */
    yearlyOriginalPrice?: number;
    /** 年付售价 */
    yearlySalePrice?: number;
    /** 应急包原价 */
    emergencyPackOriginalPrice?: number;
    /** 应急包售价 */
    emergencyPackSalePrice?: number;
    /** 应急包折扣 */
    emergencyPackSaleDiscount?: number;
    /** 项目包原价 */
    projectPackOriginalPrice?: number;
    /** 项目包售价 */
    projectPackSalePrice?: number;
    /** 项目包折扣 */
    projectPackSaleDiscount?: number;
}

